import type {ISketchObject} from '../../mesh/sketch/ISketchObject';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import {SketchEntitySymmetricConstraint}
    from '../solver/constraint/relationDefinition/SketchEntitySymmetricConstraint';
import type {SketchActiveParametricStateSolveInput}
    from '../../../types/sketch/solver/constraint/SketchRelationCreationService';
import type {SketchParametricSolveCoordinatorResult}
    from '../../../types/sketch/solver/SketchParametricSolveCoordinator';
import type {SketchMirrorAxisReferenceProvider} from './SketchMirrorAxisReferenceProvider';
import {SketchEntityDefinitionAdapter} from './SketchEntityDefinitionAdapter';
import type {
    SketchMirrorAxisReferenceReconcileDiagnostic,
    SketchMirrorAxisReferenceReconcileResult,
} from '../../../types/sketch/mirror/SketchMirrorAxisReferenceTypes';

/** 外部镜像轴参数重算完成后的统一草图定义提交原因。 */
const MIRROR_AXIS_REFERENCE_RECONCILED_COMMIT_REASON = 'mirror-axis-reference-reconciled';
/** 参数变化判定公差。 */
const PARAMETER_CHANGE_TOLERANCE = 1e-9;

/**
 * 在外部模型或基准引用刷新后重算镜像派生关系。
 *
 * 服务只负责建立外部轴依赖图和本轮权威根，求解、尺寸装配、版本门禁与原子写回
 * 全部经 Viewer 的统一参数协调入口完成，禁止维护第二套 island runtime service。
 */
export class SketchMirrorAxisReferenceReconcileService {
    /** 无外部引用时跨调用复用的零分配结果。 */
    public static readonly emptyResult: SketchMirrorAxisReferenceReconcileResult = Object.freeze({
        reconciledConstraintIds: Object.freeze([]),
        danglingProviderIds: Object.freeze([]),
        diagnostics: Object.freeze([]),
    });
    /** 统一解析各实体完整控制点域。 */
    private readonly entityDefinitionAdapter = new SketchEntityDefinitionAdapter();

    /**
     * 重算指定草图组内的外部轴镜像关系。
     *
     * @param sketchGroups 已完成上游引用重建的草图组。
     * @param solveParametricState Viewer 统一参数求解端口。
     * @returns 成功关系、悬空 provider 与结构化诊断。
     */
    public reconcile(
        sketchGroups: readonly SketchGroup[],
        solveParametricState: (
            input: SketchActiveParametricStateSolveInput,
        ) => SketchParametricSolveCoordinatorResult,
    ): SketchMirrorAxisReferenceReconcileResult {
        if (!sketchGroups.some((group) => group.getSketchReferenceTargets().size > 0)) {
            return SketchMirrorAxisReferenceReconcileService.emptyResult;
        }

        const reconciledConstraintIds = new Set<string>();
        const danglingProviderIds = new Set<string>();
        const diagnostics: SketchMirrorAxisReferenceReconcileDiagnostic[] = [];

        sketchGroups.forEach((sketchGroup) => {
            const providers = [...sketchGroup.getSketchReferenceTargets()];
            if (providers.length === 0) {
                return;
            }
            this.runWithProviderResolutionScopes(providers, 0, () => {
                this.reconcileSketchGroup(
                    sketchGroup,
                    providers,
                    solveParametricState,
                    reconciledConstraintIds,
                    danglingProviderIds,
                    diagnostics,
                );
            });
        });

        return {
            reconciledConstraintIds: [...reconciledConstraintIds].sort(),
            danglingProviderIds: [...danglingProviderIds].sort(),
            diagnostics,
        };
    }

    /**
     * 在同一草图全部 provider 的短生命周期解析作用域内执行重算。
     *
     * @param providers 当前草图的外部轴 provider。
     * @param index 当前待进入作用域的索引。
     * @param work 全部作用域建立后执行的工作。
     */
    private runWithProviderResolutionScopes(
        providers: readonly SketchMirrorAxisReferenceProvider[],
        index: number,
        work: () => void,
    ): void {
        const provider = providers[index];
        if (!provider) {
            work();
            return;
        }
        provider.runWithMirrorAxisResolutionScope(() => {
            this.runWithProviderResolutionScopes(providers, index + 1, work);
        });
    }

    /**
     * 聚合同一草图的全部有效外部镜像关系并执行一次统一参数求解。
     *
     * @param sketchGroup 当前草图。
     * @param providers 当前草图全部 provider。
     * @param solveParametricState Viewer 统一参数求解端口。
     * @param reconciledConstraintIds 全局成功关系集合。
     * @param danglingProviderIds 全局悬空 provider 集合。
     * @param diagnostics 全局诊断列表。
     */
    private reconcileSketchGroup(
        sketchGroup: SketchGroup,
        providers: readonly SketchMirrorAxisReferenceProvider[],
        solveParametricState: (
            input: SketchActiveParametricStateSolveInput,
        ) => SketchParametricSolveCoordinatorResult,
        reconciledConstraintIds: Set<string>,
        danglingProviderIds: Set<string>,
        diagnostics: SketchMirrorAxisReferenceReconcileDiagnostic[],
    ): void {
        const dependencies: Array<readonly [
            SketchEntitySymmetricConstraint,
            SketchMirrorAxisReferenceProvider,
            ISketchObject,
            ISketchObject,
        ]> = [];
        const entityByPersistentId = new Map<string, ISketchObject>();
        const incomingDegreeByEntityId = new Map<string, number>();
        const outgoingEntityIdsByEntityId = new Map<string, string[]>();

        providers.forEach((provider) => {
            const providerRelations = provider.constraints.getRelations();
            if (!provider.resolveMirrorAxisGeometry()) {
                const message = `外部镜像轴来源已悬空或不再形成唯一二维直线: ${provider.name}`;
                danglingProviderIds.add(provider.persistentId);
                diagnostics.push({
                    providerId: provider.persistentId,
                    referenceId: provider.getReference().referenceId,
                    message,
                });
                providerRelations.forEach((relation) => relation.markDangling(message));
                return;
            }

            providerRelations.forEach((relation) => {
                if (!(relation instanceof SketchEntitySymmetricConstraint)) {
                    return;
                }
                const targets = relation.getTargetsSnapshot();
                const sourceEntity = targets[1];
                const mirroredEntity = targets[2];
                if (!sourceEntity || !mirroredEntity) {
                    const message = `外部镜像轴关系缺少源实体或镜像实体: ${provider.name}`;
                    relation.markDangling(message);
                    diagnostics.push({
                        providerId: provider.persistentId,
                        referenceId: provider.getReference().referenceId,
                        message,
                    });
                    return;
                }
                relation.clearDangling();
                dependencies.push([relation, provider, sourceEntity, mirroredEntity]);
                entityByPersistentId.set(sourceEntity.persistentId, sourceEntity);
                entityByPersistentId.set(mirroredEntity.persistentId, mirroredEntity);
                incomingDegreeByEntityId.set(
                    sourceEntity.persistentId,
                    incomingDegreeByEntityId.get(sourceEntity.persistentId) ?? 0,
                );
                incomingDegreeByEntityId.set(
                    mirroredEntity.persistentId,
                    (incomingDegreeByEntityId.get(mirroredEntity.persistentId) ?? 0) + 1,
                );
                const outgoingIds = outgoingEntityIdsByEntityId.get(sourceEntity.persistentId) ?? [];
                outgoingIds.push(mirroredEntity.persistentId);
                outgoingEntityIdsByEntityId.set(sourceEntity.persistentId, outgoingIds);
            });
        });

        if (dependencies.length === 0) {
            return;
        }

        const acyclicEntityIds = this.resolveAcyclicEntityIds(
            entityByPersistentId,
            incomingDegreeByEntityId,
            outgoingEntityIdsByEntityId,
        );
        const seedObjects = new Set<ISketchObject>();
        const authoritativeEntityIds = new Set<string>();
        const authoritativePointIds = new Set<string>();
        const priorityAliases = new Map<string, string>();
        const groupConstraintIds: string[] = [];

        dependencies.forEach(([relation, provider, sourceEntity, mirroredEntity]) => {
            if (
                !acyclicEntityIds.has(sourceEntity.persistentId)
                || !acyclicEntityIds.has(mirroredEntity.persistentId)
            ) {
                const message = `外部镜像轴关系图存在无权威根的环状依赖，已跳过关系 ${relation.persistentId}`;
                relation.markDangling(message);
                diagnostics.push({
                    code: 'cyclic-symmetry-dependency',
                    providerId: provider.persistentId,
                    referenceId: provider.getReference().referenceId,
                    message,
                });
                return;
            }

            seedObjects.add(provider);
            seedObjects.add(sourceEntity);
            seedObjects.add(mirroredEntity);
            groupConstraintIds.push(relation.persistentId);
            if ((incomingDegreeByEntityId.get(sourceEntity.persistentId) ?? 0) === 0) {
                authoritativeEntityIds.add(sourceEntity.persistentId);
                this.entityDefinitionAdapter.resolveControlPoints(sourceEntity).forEach((point) => {
                    authoritativePointIds.add(point.persistentId);
                    priorityAliases.set(point.persistentId, point.uuid);
                });
            }
        });

        if (groupConstraintIds.length === 0 || seedObjects.size === 0) {
            return;
        }
        seedObjects.forEach((object) => priorityAliases.set(object.persistentId, object.uuid));

        const outcome = solveParametricState({
            targetSketchId: sketchGroup.persistentId,
            seedObjects: [...seedObjects],
            operationIntents: [],
            authoritativePointIds: [...authoritativePointIds],
            authoritativeEntityIds: [...authoritativeEntityIds],
            priorityAliases,
            reason: MIRROR_AXIS_REFERENCE_RECONCILED_COMMIT_REASON,
            markDefinitionDirty: false,
        });
        if (!outcome.applied) {
            const providerIds = new Set<string>();
            dependencies.forEach(([, provider]) => {
                if (providerIds.has(provider.persistentId)) {
                    return;
                }
                providerIds.add(provider.persistentId);
                diagnostics.push({
                    providerId: provider.persistentId,
                    referenceId: provider.getReference().referenceId,
                    message: `外部镜像轴关系参数重算失败: ${outcome.message ?? provider.name}`,
                });
            });
            return;
        }

        if (this.hasParameterChanges(outcome)) {
            const changedObjectIds = [...seedObjects].map((object) => object.uuid);
            const metadata = {
                reason: MIRROR_AXIS_REFERENCE_RECONCILED_COMMIT_REASON,
                sourceObjectId: changedObjectIds[0],
                changedObjectIds,
            };
            sketchGroup.markDefinitionDirty(metadata);
            // 上游模型重建事务负责 Undo；这里只发布一次草图定义提交以刷新持久化和下游特征。
            sketchGroup.commitDefinitionChanges(metadata);
        }
        groupConstraintIds.forEach((constraintId) => reconciledConstraintIds.add(constraintId));
    }

    /**
     * 按镜像依赖图解析全部可从权威根到达的实体。
     * @param entities 图中的实体。
     * @param incomingDegrees 实体入度。
     * @param outgoingIds 实体出边。
     * @returns 非环状依赖内的实体 ID。
     */
    private resolveAcyclicEntityIds(
        entities: ReadonlyMap<string, ISketchObject>,
        incomingDegrees: ReadonlyMap<string, number>,
        outgoingIds: ReadonlyMap<string, readonly string[]>,
    ): Set<string> {
        const remainingIncomingDegrees = new Map(incomingDegrees);
        const queue = [...entities.keys()].filter(
            (entityId) => (remainingIncomingDegrees.get(entityId) ?? 0) === 0,
        );
        const resolvedIds = new Set<string>();
        for (let index = 0; index < queue.length; index += 1) {
            const entityId = queue[index];
            if (!entityId || resolvedIds.has(entityId)) {
                continue;
            }
            resolvedIds.add(entityId);
            (outgoingIds.get(entityId) ?? []).forEach((targetId) => {
                const nextDegree = (remainingIncomingDegrees.get(targetId) ?? 0) - 1;
                remainingIncomingDegrees.set(targetId, nextDegree);
                if (nextDegree === 0) {
                    queue.push(targetId);
                }
            });
        }
        return resolvedIds;
    }

    /**
     * 判断统一求解前后的参数向量是否实际变化。
     * @param outcome 协调器结果。
     * @returns 任一稳定参数发生超公差变化时返回 true。
     */
    private hasParameterChanges(outcome: SketchParametricSolveCoordinatorResult): boolean {
        const beforeEntries = outcome.beforeIsland?.parameterVector.entries ?? [];
        const afterEntries = outcome.solveResult?.island.parameterVector.entries ?? [];
        if (beforeEntries.length !== afterEntries.length) {
            return true;
        }
        const beforeValueById = new Map(beforeEntries.map((entry) => [entry.parameterId, entry.value]));
        return afterEntries.some((entry) => {
            const beforeValue = beforeValueById.get(entry.parameterId);
            return beforeValue === undefined
                || Math.abs(beforeValue - entry.value) > PARAMETER_CHANGE_TOLERANCE;
        });
    }
}
